import type {
  AxialCoord,
  BuildKind,
  Command,
  FactionId,
  MayorStance,
  SeatId,
  SeatRole,
  UnitKind,
} from "../core";

/**
 * What crosses the wire between a client and a room (B3.4, ADR-0016, the plan's protocol). Types
 * from core only, never its code: the server that parses these never runs the core (D-023).
 * Commands, never state: every client applies the same turns through its own door.
 *
 * The file in four parts: the messages' types; the readers of untrusted input (shared with
 * `events.ts`, the Worker and the page's knobs: this file may import no code, so they live
 * here); the command table (one line a verb); the messages' parsers.
 */

/**
 * A command and the number of the seat of the connection it came from, stamped by the room
 * (K0, ADR-0017 point 2): the core resolves the seat, so the room never needs a soldier's unit.
 */
export interface StampedCommand {
  readonly seat: SeatId;
  readonly command: Command;
}

/**
 * From the turn that carries it on, the seats humans hold (B3.7, K0): a human who left hands the
 * seat to its script, one who comes back takes it again. Every page applies it at the same turn,
 * so the scripts take over, and give back, at the same tick everywhere.
 */
export interface HeldChange {
  readonly held: readonly SeatId[];
}

/** One entry of a turn: a stamped command, or a change of who holds the seats. */
export type TurnEntry = StampedCommand | HeldChange;

/**
 * K4: a match's state at a tick, as the JSON text one voter of the room's settled hash sent it
 * (the very text its vote hashed), and that hash. The room keeps one only if the text hashes to
 * it; a page that comes in under way, or out of step, starts from it and takes the turns after.
 * K7.1: the hash is the text's SHA-256 (`sha256.ts`), so a forged text cannot pass for the vote.
 */
export interface Snapshot {
  readonly tick: number;
  readonly hash: string;
  readonly body: string;
}

/** A seat of a room's table (K0): its number, kingdom and role, as the run lays them out. */
export interface TableSeat {
  readonly seat: SeatId;
  readonly faction: FactionId;
  readonly role: SeatRole;
}

/** Who sits where; `name` null: a script holds the seat. */
export interface SeatedPlayer extends TableSeat {
  readonly name: string | null;
}

/**
 * The ways a room plays (K2): the King's duel (one King a kingdom), the company duel (K1: a
 * King and four soldier seats a kingdom), two against two (K6.1: K6's four Kings
 * on two sides), or the war (K7.1: K7's twenty Kings on two sides of ten). A mode fixes the room's
 * table and sides (`ROOM_TABLES`, `ROOM_SIDES`, `modes.ts`) and the run every page builds from
 * the seed.
 */
export type RoomMode = "duel" | "company" | "allies" | "war";

/** A public game on the lobby's list (B3.5; K2: of any mode). */
export interface PublicGame {
  readonly id: string;
  readonly seedText: string;
  readonly mode: RoomMode;
  readonly seats: number;
  readonly humans: number;
  /** Null while it is held (nobody seated) or queued behind the head game. */
  readonly startsAt: number | null;
}

/**
 * The lobby socket (B3.5): a page says hello and hears the whole list, then the whole list again
 * on every change. Three duels are smaller than any delta worth its own message (D-045); private
 * rooms ("create") come with the row that builds them.
 */
export interface LobbyClient {
  readonly type: "hello";
  readonly playerId: string;
  readonly name: string;
  /** The page's build (its commit), for the day a server and an old page disagree. */
  readonly build: string;
}

export interface LobbyServer {
  readonly type: "games";
  /** The server's clock when it sent the list: the page reads `startsAt` against it. */
  readonly serverTime: number;
  readonly games: readonly PublicGame[];
}

export type RoomClient =
  | {
      readonly type: "join";
      readonly playerId: string;
      readonly name: string;
      /** The last turn the client holds, when it comes back (D-026). */
      readonly lastTurn?: number;
      /** K2: the seat the player asks for; the room seats it there when balance allows (`seatFor`). */
      readonly wants?: SeatRole;
      /** K2: a party's word, the same for its players: they sit in one kingdom. */
      readonly party?: string;
      /**
       * K4: seat me only in a match under way (a room's link opened late); a room still waiting
       * for its start refuses it as "open", and the page goes to the front page instead.
       */
      readonly late?: true;
      /**
       * NM2: a signed-in page's session (`server/session.ts`). A room seats a name another account
       * holds only under the session of that account; the room itself never reads it.
       */
      readonly session?: string;
    }
  | { readonly type: "command"; readonly command: Command }
  /** The page's vote on its state at the tick: the SHA-256 of its text (K7.1; FNV-1a's 32 bits before). */
  | { readonly type: "hash"; readonly tick: number; readonly hash: string }
  /** K4: the page's state at the tick the room offered, the text its vote there hashed. */
  | { readonly type: "snapshot"; readonly tick: number; readonly body: string }
  /** The page's clock in ms, sent back at once as `pong`: the socket's round trip (B3.6). */
  | { readonly type: "ping"; readonly sentAt: number }
  /**
   * K4.1: before the start, a seated human's Start now: once every human at the table asked, the
   * lobby starts the game `earlyStartMs` later, its empty seats its scripts'.
   */
  | { readonly type: "ready" }
  | { readonly type: "leave" };

export type RejectReason =
  | "shape"
  | "size"
  | "rate"
  | "full"
  | "started"
  | "unseated"
  | "unknown"
  /** K4: a late join to a room still waiting for its start. */
  | "open";

export type RoomServer =
  | {
      readonly type: "seated";
      readonly seat: SeatId;
      readonly table: readonly SeatedPlayer[];
      readonly seedText: string;
      /** K2: the room's mode; a room before K2 sends none and plays the duel. */
      readonly mode: RoomMode;
      /**
       * K4.1: how many seated humans asked to start now. A Worker before K4.1 sends none and takes
       * no ask: the page offers no Start now.
       */
      readonly ready?: number;
    }
  | {
      readonly type: "table";
      readonly table: readonly SeatedPlayer[];
      /** K4.1: as on `seated`. */
      readonly ready?: number;
    }
  | {
      readonly type: "start";
      readonly seedText: string;
      readonly held: readonly SeatId[];
      readonly mode: RoomMode;
      /**
       * K4: to a page that comes in under way or back from a reload, the room's latest snapshot;
       * the turns that follow begin at its tick. Absent: the page builds the run from the seed.
       */
      readonly snapshot?: Snapshot;
    }
  | {
      readonly type: "turns";
      /** The index of the first turn; turn n is applied at tick n. */
      readonly from: number;
      readonly turns: readonly (readonly TurnEntry[])[];
    }
  | { readonly type: "desync"; readonly tick: number }
  /** K4: send your state at this tick (the room's majority voted it); the reply is a snapshot. */
  | { readonly type: "offer"; readonly tick: number }
  /** K4: to a page out of step: start again from this snapshot; the turns after it follow. */
  | { readonly type: "resync"; readonly snapshot: Snapshot }
  | { readonly type: "pong"; readonly sentAt: number }
  | { readonly type: "ended"; readonly reason: "finished" | "abandoned" }
  | { readonly type: "rejected"; readonly reason: RejectReason };

/** A game as the lobby lists it, its code its seed text (D-028); the server's list and the page's. */
export function publicGame(
  mode: RoomMode,
  id: string,
  seats: number,
  humans: number,
  startsAt: number | null,
): PublicGame {
  return { id, seedText: id, mode, seats, humans, startsAt };
}

/**
 * A mode's seats by formula (K0, ADR-0017 point 1), as `createRun` lays them out
 * (`seatPlaces`), for the room, which never runs the core: kingdom-major, each kingdom's King
 * and then its `soldierSeats[i]` soldier seats, a seat's number its place.
 */
export function seatTable(
  kingdoms: readonly FactionId[],
  soldierSeats: readonly number[] = [],
): TableSeat[] {
  const table: TableSeat[] = [];
  kingdoms.forEach((faction, index) => {
    table.push({ seat: table.length, faction, role: "king" });
    for (let soldier = 0; soldier < (soldierSeats[index] ?? 0); soldier += 1) {
      table.push({ seat: table.length, faction, role: "soldier" });
    }
  });
  return table;
}

/** A public duel's table: the two Kings' seats (D-028). */
export const DUEL_TABLE: readonly TableSeat[] = seatTable(["player", "enemy"]);

/**
 * The list of a string union's members, checked at compile time to hold every one: a value core
 * adds breaks the build here, not a client's door on a match.
 */
function allOf<T extends string>() {
  return <const L extends readonly T[]>(list: L & ([T] extends [L[number]] ? unknown : never)) =>
    list;
}

const UNIT_KINDS = allOf<UnitKind>()(["spear", "sword", "archer", "rider", "siege"]);
/** Every kingdom in the run's order (core's `FACTION_IDS`): a room's table lays out its first ones. */
export const FACTIONS = allOf<FactionId>()([
  "player",
  "enemy",
  "seat3",
  "seat4",
  "seat5",
  "seat6",
  "seat7",
  "seat8",
  "seat9",
  "seat10",
  "seat11",
  "seat12",
  "seat13",
  "seat14",
  "seat15",
  "seat16",
  "seat17",
  "seat18",
  "seat19",
  "seat20",
  "seat21",
  "seat22",
  "seat23",
  "seat24",
  "seat25",
  "seat26",
  "seat27",
  "seat28",
  "seat29",
  "seat30",
  "seat31",
  "seat32",
]);
const SEAT_ROLES = allOf<SeatRole>()(["king", "soldier"]);
export const ROOM_MODES = allOf<RoomMode>()(["duel", "company", "allies", "war"]);
const REJECT_REASONS = allOf<RejectReason>()([
  "shape",
  "size",
  "rate",
  "full",
  "started",
  "unseated",
  "unknown",
  "open",
]);
const BUILD_KINDS = allOf<BuildKind>()(["tower", "city", "farm", "hamlet"]);
const STANCES = allOf<MayorStance>()(["save", "garrison", "muster", "develop"]);

/** Far beyond any map the game makes (601 hexes reach a radius of about 20). */
const MAX_COORD = 1_000;
/** The longest ID, code, seed text or build a message may carry. */
export const MAX_ID_CHARS = 64;
const MAX_NAME_CHARS = 32;
/** A party's word: a few letters friends agree on, or a link's. */
const MAX_PARTY_CHARS = 32;
/** NM2: a session the Worker makes: the account's code, its expiry and a seal, with room to spare. */
export const MAX_SESSION_CHARS = 160;
const MAX_BUILD_CHARS = 64;
/** K7.1: a vote, a SHA-256 in lowercase hex. */
const DIGEST = /^[0-9a-f]{64}$/;
/** Far beyond any table a mode lays out (the war's is 100 seats, ADR-0017). */
const MAX_SEAT = 1_000;

/** An untrusted JSON object's fields, each still to be read. */
export type Fields = Record<string, unknown>;

/** A JSON object, not an array and not null. */
export function isRecord(value: unknown): value is Fields {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A whole number from 0 to `max`, or undefined. */
export function count(value: unknown, max = Number.MAX_SAFE_INTEGER): number | undefined {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= max
    ? value
    : undefined;
}

function coord(value: unknown): AxialCoord | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const { q, r } = value;
  return typeof q === "number" &&
    typeof r === "number" &&
    Number.isInteger(q) &&
    Number.isInteger(r) &&
    Math.abs(q) <= MAX_COORD &&
    Math.abs(r) <= MAX_COORD
    ? { q, r }
    : undefined;
}

/** The member of `list` the value is, or undefined. */
export function oneOf<T extends string>(list: readonly T[], value: unknown): T | undefined {
  return list.find((member) => member === value);
}

/** A string of 1 to `max` characters, or undefined. */
export function text(value: unknown, max: number): string | undefined {
  return typeof value === "string" && value.length > 0 && value.length <= max ? value : undefined;
}

/** The JSON object a message is, or undefined. */
export function jsonRecord(raw: string): Fields | undefined {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return undefined;
  }
  return isRecord(value) ? value : undefined;
}

/** Every item of a list read by `read`, or undefined when one of them is not one. */
export function listOf<T>(value: unknown, read: (item: unknown) => T | undefined): T[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const items: T[] = [];
  for (const item of value) {
    const entry = read(item);
    if (entry === undefined) {
      return undefined;
    }
    items.push(entry);
  }
  return items;
}

/** A positive whole number from text (an address knob, a Worker variable), or undefined. */
export function positiveInt(raw: string | null | undefined): number | undefined {
  const value = Number.parseInt(raw ?? "", 10);
  return Number.isInteger(value) && value > 0 ? value : undefined;
}

/** How each field a command may carry is read; a field's name says what it holds. */
const FIELD_READERS = {
  unitId: (value: unknown) => count(value),
  commanderId: (value: unknown) => count(value),
  to: coord,
  at: coord,
  city: coord,
  from: coord,
  unit: (value: unknown) => oneOf(UNIT_KINDS, value),
  structure: (value: unknown) => oneOf(BUILD_KINDS, value),
  stance: (value: unknown) => oneOf(STANCES, value),
};
type FieldName = keyof typeof FIELD_READERS;
/** The fields a verb carries besides its kind. */
type VerbField<K extends Command["kind"]> = Exclude<keyof Extract<Command, { kind: K }>, "kind"> &
  FieldName;

/** A field a command may leave out; when it is there it must read. */
const OPTIONAL_FIELDS: ReadonlySet<FieldName> = new Set(["from"]);

/**
 * Each verb's fields in the order the command carries them: the order is the command's bytes
 * (its key order reaches the state the pages hash), so it follows the `Command` union's.
 */
const COMMAND_FIELDS: { readonly [K in Command["kind"]]: readonly VerbField<K>[] } = {
  order: ["unitId", "to"],
  stop: ["unitId"],
  train: ["city", "unit"],
  build: ["at", "structure"],
  raze: ["at"],
  govern: ["unitId", "city"],
  dismiss: ["city"],
  promote: ["unitId"],
  demote: ["unitId"],
  aim: ["unitId", "at"],
  stance: ["city", "stance"],
  mark: ["at", "from"],
  clearMark: ["at"],
  call: [],
  clearCall: [],
  tax: [],
  rush: ["city"],
  moveKing: ["to"],
  stopKing: [],
  sell: ["unitId"],
  enlist: ["unitId", "commanderId"],
  command: ["unitId"],
  standDown: ["unitId"],
  deploy: ["city"],
};

/**
 * A command a client's door can judge, rebuilt from its checked fields: each verb's own, in its
 * order; anything else on the object is dropped. Undefined when a field does not read.
 */
function parseCommand(value: unknown): Command | undefined {
  if (
    !isRecord(value) ||
    typeof value.kind !== "string" ||
    !Object.hasOwn(COMMAND_FIELDS, value.kind)
  ) {
    return undefined;
  }
  const kind = value.kind as Command["kind"];
  const command: Fields = { kind };
  for (const name of COMMAND_FIELDS[kind]) {
    if (OPTIONAL_FIELDS.has(name) && value[name] === undefined) {
      continue;
    }
    const field = FIELD_READERS[name](value[name]);
    if (field === undefined) {
      return undefined;
    }
    command[name] = field;
  }
  return command as Command;
}

/** A seat's number (K0); since K0W the shapes of the Worker before K0 are no seat. */
function seatId(value: unknown): SeatId | undefined {
  return count(value, MAX_SEAT);
}

function seatIds(value: unknown): SeatId[] | undefined {
  return listOf(value, seatId);
}

function seatedPlayer(value: unknown): SeatedPlayer | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const faction = oneOf(FACTIONS, value.faction);
  const name = value.name === null ? null : text(value.name, MAX_NAME_CHARS);
  const seat = seatId(value.seat);
  const role = oneOf(SEAT_ROLES, value.role);
  return faction && name !== undefined && seat !== undefined && role
    ? { seat, faction, role, name }
    : undefined;
}

/** A room's table, row by row. */
function seatedTable(value: unknown): SeatedPlayer[] | undefined {
  return listOf(value, seatedPlayer);
}

function listedGame(value: unknown): PublicGame | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const mode = oneOf(ROOM_MODES, value.mode);
  const id = text(value.id, MAX_ID_CHARS);
  const seedText = text(value.seedText, MAX_ID_CHARS);
  const seats = count(value.seats);
  const humans = count(value.humans);
  const startsAt = value.startsAt === null ? null : count(value.startsAt);
  return mode &&
    id &&
    seedText &&
    seats !== undefined &&
    humans !== undefined &&
    startsAt !== undefined
    ? { id, seedText, mode, seats, humans, startsAt }
    : undefined;
}

/**
 * The lobby's games a page can play: a game of a mode a later server lists and this page does not
 * know is left off the list, not a reason to read none of it (K2's company cards to a page of
 * before K2 the same way).
 */
function listedGames(value: unknown): PublicGame[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const known = value.filter(
    (item) => !isRecord(item) || typeof item.mode !== "string" || oneOf(ROOM_MODES, item.mode),
  );
  return listOf(known, listedGame);
}

/**
 * K4.1: a table's count of asks to start now; undefined when it names none, false when it does
 * not read.
 */
function askedOf(value: unknown): number | undefined | false {
  if (value === undefined) {
    return undefined;
  }
  return count(value, MAX_SEAT) ?? false;
}

/** A room's mode (K2); since K0W a room that names none is no room of this page's. */
function roomMode(value: unknown): RoomMode | undefined {
  return oneOf(ROOM_MODES, value);
}

/** K7.1: a SHA-256 digest as the pages write it, or undefined. */
function digest(value: unknown): string | undefined {
  return typeof value === "string" && DIGEST.test(value) ? value : undefined;
}

/** A state's text: any length the room let through, never empty. */
function body(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function snapshotOf(value: unknown): Snapshot | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const tick = count(value.tick);
  const hash = digest(value.hash);
  const text = body(value.body);
  return tick !== undefined && hash !== undefined && text !== undefined
    ? { tick, hash, body: text }
    : undefined;
}

function turnEntry(item: unknown): TurnEntry | undefined {
  if (!isRecord(item)) {
    return undefined;
  }
  if (item.held !== undefined) {
    const held = seatIds(item.held);
    return held ? { held } : undefined;
  }
  const by = seatId(item.seat);
  const command = parseCommand(item.command);
  return by !== undefined && command ? { seat: by, command } : undefined;
}

/** One message from a page to the lobby, checked and rebuilt; undefined when it is not one. */
export function parseLobbyClient(raw: string): LobbyClient | undefined {
  const value = jsonRecord(raw);
  if (value?.type !== "hello") {
    return undefined;
  }
  const playerId = text(value.playerId, MAX_ID_CHARS);
  const name = text(value.name, MAX_NAME_CHARS);
  const build = text(value.build, MAX_BUILD_CHARS);
  return playerId && name && build ? { type: "hello", playerId, name, build } : undefined;
}

/** The lobby's list as the page reads it; undefined when the message is not one. */
export function parseLobbyServer(raw: string): LobbyServer | undefined {
  const value = jsonRecord(raw);
  if (value?.type !== "games") {
    return undefined;
  }
  const serverTime = count(value.serverTime);
  const games = listedGames(value.games);
  return serverTime !== undefined && games ? { type: "games", serverTime, games } : undefined;
}

/** One message from a room as the page reads it; undefined when it is not one. */
export function parseRoomServer(raw: string): RoomServer | undefined {
  const value = jsonRecord(raw);
  if (!value) {
    return undefined;
  }
  switch (value.type) {
    case "seated": {
      const at = seatId(value.seat);
      const table = seatedTable(value.table);
      const seedText = text(value.seedText, MAX_ID_CHARS);
      const mode = roomMode(value.mode);
      const ready = askedOf(value.ready);
      return at !== undefined && table && seedText && mode && ready !== false
        ? {
            type: "seated",
            seat: at,
            table,
            seedText,
            mode,
            ...(ready === undefined ? {} : { ready }),
          }
        : undefined;
    }
    case "table": {
      const table = seatedTable(value.table);
      const ready = askedOf(value.ready);
      return table && ready !== false
        ? { type: "table", table, ...(ready === undefined ? {} : { ready }) }
        : undefined;
    }
    case "start": {
      const seedText = text(value.seedText, MAX_ID_CHARS);
      const held = seatIds(value.held);
      const mode = roomMode(value.mode);
      const snapshot = value.snapshot === undefined ? undefined : snapshotOf(value.snapshot);
      if (!seedText || !held || !mode || (value.snapshot !== undefined && !snapshot)) {
        return undefined;
      }
      return { type: "start", seedText, held, mode, ...(snapshot ? { snapshot } : {}) };
    }
    case "offer": {
      const tick = count(value.tick);
      return tick === undefined ? undefined : { type: "offer", tick };
    }
    case "resync": {
      const snapshot = snapshotOf(value.snapshot);
      return snapshot ? { type: "resync", snapshot } : undefined;
    }
    case "turns": {
      const from = count(value.from);
      const turns = listOf(value.turns, (turn) => listOf(turn, turnEntry));
      return from !== undefined && turns ? { type: "turns", from, turns } : undefined;
    }
    case "desync": {
      const tick = count(value.tick);
      return tick === undefined ? undefined : { type: "desync", tick };
    }
    case "pong": {
      const sentAt = count(value.sentAt);
      return sentAt === undefined ? undefined : { type: "pong", sentAt };
    }
    case "ended":
      return value.reason === "finished" || value.reason === "abandoned"
        ? { type: "ended", reason: value.reason }
        : undefined;
    case "rejected": {
      const reason = oneOf(REJECT_REASONS, value.reason);
      return reason ? { type: "rejected", reason } : undefined;
    }
    default:
      return undefined;
  }
}

/** One message from a client to a room, checked and rebuilt; undefined when it is not one. */
export function parseRoomClient(raw: string): RoomClient | undefined {
  const value = jsonRecord(raw);
  if (!value) {
    return undefined;
  }
  switch (value.type) {
    case "join": {
      const playerId = text(value.playerId, MAX_ID_CHARS);
      const name = text(value.name, MAX_NAME_CHARS);
      const lastTurn = value.lastTurn === undefined ? undefined : count(value.lastTurn);
      const wants = value.wants === undefined ? undefined : oneOf(SEAT_ROLES, value.wants);
      const party = value.party === undefined ? undefined : text(value.party, MAX_PARTY_CHARS);
      const session =
        value.session === undefined ? undefined : text(value.session, MAX_SESSION_CHARS);
      if (
        !playerId ||
        !name ||
        (value.lastTurn !== undefined && lastTurn === undefined) ||
        (value.wants !== undefined && wants === undefined) ||
        (value.party !== undefined && party === undefined) ||
        (value.session !== undefined && session === undefined) ||
        (value.late !== undefined && value.late !== true)
      ) {
        return undefined;
      }
      return {
        type: "join",
        playerId,
        name,
        ...(lastTurn === undefined ? {} : { lastTurn }),
        ...(wants === undefined ? {} : { wants }),
        ...(party === undefined ? {} : { party }),
        ...(value.late === true ? { late: true } : {}),
        ...(session === undefined ? {} : { session }),
      };
    }
    case "command": {
      const command = parseCommand(value.command);
      return command ? { type: "command", command } : undefined;
    }
    case "hash": {
      const tick = count(value.tick);
      const hash = digest(value.hash);
      return tick === undefined || hash === undefined ? undefined : { type: "hash", tick, hash };
    }
    case "snapshot": {
      const tick = count(value.tick);
      const text = body(value.body);
      return tick === undefined || text === undefined
        ? undefined
        : { type: "snapshot", tick, body: text };
    }
    case "ping": {
      const sentAt = count(value.sentAt);
      return sentAt === undefined ? undefined : { type: "ping", sentAt };
    }
    case "ready":
      return { type: "ready" };
    case "leave":
      return { type: "leave" };
    default:
      return undefined;
  }
}
